// BWA-Konzern-Aggregation (Sprint 7 Push 2B).
//
// Hero-KPI-Tiles (Umsatz / EBIT) brauchen:
//  - Konzern-YTD (Sum ueber Holding-Firmen, OHNE InfraX — R1).
//  - Monatlichen Verlauf fuer Sparkline (letzte N Monate).
//  - Vergleich zum gleichen Stichtag im Vorjahr (vs-PY-Pill).
//
// Alle BWA-Werte kommen als String (Decimal-Treue, siehe python-style.md).
// Wir parsen einmal hier, exportieren Number — Anzeige-Layer rundet via
// formatEUR.

import type { BwaItem } from "./api";

export type Severity = "ok" | "warn" | "critical";

// Sprint-7-Push 6: InfraX im Aggregator. Kevin 2026-05-13:
// Umsatz/Gewinn-Aggregate sind nicht Bahn-sensibel. Banking/Forderungen
// bleiben weiter R1-getrennt (Whitelist im Cockpit-Aggregator-Endpoint).
const HOLDING_FIRMEN = new Set(["xholding", "drainx", "gundk", "infrax"]);

export interface BwaVorjahrVergleich {
  umsatzYtdPriorYear: number;
  umsatzDeltaPct: number | null; // null wenn prior=0
  ebitYtdPriorYear: number;
  ebitDeltaAbs: number;
}

export interface BwaKonzernAggregate {
  /** Aktuellster Stichtag (max. jahr/monat ueber Holding-Firmen). */
  jahr: number;
  bisMonat: number; // 1..12
  /** "01-04/2026" — Stichtag-Label fuer KPI-Subtitle. */
  periodLabel: string;
  /** YTD Konzern-Sum. */
  umsatzYtd: number;
  ebitYtd: number;
  /** EBIT-Marge in Prozent (ebit/umsatz*100). null wenn umsatz=0. */
  margePct: number | null;
  /** Monatswerte (jaengste Monate rechts). Konzern-Sum pro Monat. */
  umsatzMonthly: number[];
  ebitMonthly: number[];
  vorjahr: BwaVorjahrVergleich | null;
}

const num = (s: string | null | undefined): number => {
  if (s == null || s === "") return 0;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Aggregiert BWA-Items zum Konzern-Stand.
 *
 * Returns null wenn keine Holding-Items vorliegen.
 */
export function aggregateBwaKonzern(
  items: BwaItem[] | undefined | null,
  options: { trailingMonths?: number } = {},
): BwaKonzernAggregate | null {
  if (!items || items.length === 0) return null;
  const trailing = options.trailingMonths ?? 12;

  const holdingItems = items.filter((it) => HOLDING_FIRMEN.has(it.firma_id));
  if (holdingItems.length === 0) return null;

  // Stichtag = max (jahr, monat) ueber alle Holding-Firmen — Voraussetzung
  // dass alle Firmen den gleichen aktuellen Monat geliefert haben. Wir nehmen
  // den juengsten verfuegbaren Stichtag und verwenden NUR Items aus diesem
  // Stichtag fuer YTD (nicht aus aelteren Monaten — das wuerden Doppelzaehlung).
  let maxJahr = -Infinity;
  let maxMonatInJahr = -Infinity;
  for (const it of holdingItems) {
    if (it.jahr > maxJahr || (it.jahr === maxJahr && it.monat > maxMonatInJahr)) {
      maxJahr = it.jahr;
      maxMonatInJahr = it.monat;
    }
  }
  // Pro Firma den juengsten Stichtag waehlen (nicht zwingend identisch zwischen
  // Firmen — Pahl liefert manchmal Lag). Worst-Case zaehlt jede Firma ihren
  // eigenen letzten YTD-Stand bei.
  const latestPerFirma = new Map<string, BwaItem>();
  for (const it of holdingItems) {
    const prev = latestPerFirma.get(it.firma_id);
    if (
      !prev ||
      it.jahr > prev.jahr ||
      (it.jahr === prev.jahr && it.monat > prev.monat)
    ) {
      latestPerFirma.set(it.firma_id, it);
    }
  }
  const latest = Array.from(latestPerFirma.values());

  const umsatzYtd = latest.reduce((a, it) => a + num(it.umsatz_eur_ytd), 0);
  const ebitYtd = latest.reduce(
    (a, it) => a + num(it.betriebsergebnis_eur_ytd),
    0,
  );
  const margePct = umsatzYtd > 0 ? (ebitYtd / umsatzYtd) * 100 : null;

  // Vorjahres-Vergleich: gleicher bisMonat im Vorjahr.
  const priorPerFirma = new Map<string, BwaItem>();
  for (const it of holdingItems) {
    if (it.jahr === maxJahr - 1 && it.monat === maxMonatInJahr) {
      priorPerFirma.set(it.firma_id, it);
    }
  }
  let vorjahr: BwaVorjahrVergleich | null = null;
  if (priorPerFirma.size > 0) {
    const priorList = Array.from(priorPerFirma.values());
    const umsatzPrior = priorList.reduce(
      (a, it) => a + num(it.umsatz_eur_ytd),
      0,
    );
    const ebitPrior = priorList.reduce(
      (a, it) => a + num(it.betriebsergebnis_eur_ytd),
      0,
    );
    vorjahr = {
      umsatzYtdPriorYear: umsatzPrior,
      umsatzDeltaPct:
        umsatzPrior > 0 ? ((umsatzYtd - umsatzPrior) / umsatzPrior) * 100 : null,
      ebitYtdPriorYear: ebitPrior,
      ebitDeltaAbs: ebitYtd - ebitPrior,
    };
  }

  // Monthly-Series (Konzern-Sum). Buckets per (jahr*12+monat), nur trailing
  // N Monate ab Stichtag rueckwaerts. Verwendet Monatswerte (umsatz_eur,
  // ergebnis_eur), NICHT YTD — sonst waere die "Sparkline" eine Treppe.
  const buckets = new Map<number, { umsatz: number; ebit: number }>();
  for (const it of holdingItems) {
    const key = it.jahr * 12 + (it.monat - 1);
    const prev = buckets.get(key) ?? { umsatz: 0, ebit: 0 };
    prev.umsatz += num(it.umsatz_eur);
    prev.ebit += num(it.ergebnis_eur);
    buckets.set(key, prev);
  }
  const stichtagKey = maxJahr * 12 + (maxMonatInJahr - 1);
  const umsatzMonthly: number[] = [];
  const ebitMonthly: number[] = [];
  for (let i = trailing - 1; i >= 0; i--) {
    const b = buckets.get(stichtagKey - i);
    umsatzMonthly.push(b?.umsatz ?? 0);
    ebitMonthly.push(b?.ebit ?? 0);
  }

  return {
    jahr: maxJahr,
    bisMonat: maxMonatInJahr,
    periodLabel: `01-${String(maxMonatInJahr).padStart(2, "0")}/${maxJahr}`,
    umsatzYtd,
    ebitYtd,
    margePct,
    umsatzMonthly,
    ebitMonthly,
    vorjahr,
  };
}

/** Severity fuer das EBIT-Hero-Tile: <0 critical, marge<5% warn, sonst ok. */
export function deriveEbitSeverity(agg: BwaKonzernAggregate | null): Severity {
  if (!agg) return "ok";
  if (agg.ebitYtd < 0) return "critical";
  if (agg.margePct !== null && agg.margePct < 5) return "warn";
  return "ok";
}

// ---- Sprint-7-Push 3: rollierender 3-Monats-Schnitt ---------------------
//
// Kevin: „BWAs sind fuer andere Dinge gut, wichtig waeren da immer so drei
// Monatsschnitte". Statt YTD-bis-letzter-Monat zeigen die Hero-Tiles den
// Schnitt der letzten 3 Vollmonate (Ø Umsatz, Ø EBIT, Ø Marge) plus den
// Vergleich zum 3M-Schnitt davor. Glaettet den Stichtag-Effekt.

export interface BwaRolling3MComparison {
  /** Ø Umsatz pro Monat in den 3 Monaten direkt davor (M-6..M-4). */
  umsatzPriorAvg: number;
  /** Delta in % vs aktuellem 3M-Schnitt. null wenn Prior=0. */
  umsatzDeltaPct: number | null;
  /** Ø EBIT in den 3 Monaten direkt davor. */
  ebitPriorAvg: number;
  /** Differenz in absoluten Euro (current avg − prior avg). */
  ebitDeltaAbs: number;
}

export interface BwaRolling3MAggregate {
  jahr: number;
  bisMonat: number; // 1..12 — juengster Monat der 3M-Fenster
  /** Label „02-04/2026" — Spannweite des 3M-Fensters. */
  periodLabel: string;
  /** Ø Umsatz pro Monat in den letzten 3 Vollmonaten. */
  umsatzAvg: number;
  /** Ø EBIT pro Monat in den letzten 3 Vollmonaten. */
  ebitAvg: number;
  /** Ø Marge in Prozent (ebitAvg/umsatzAvg*100). null wenn umsatz=0. */
  margePct: number | null;
  /** Konzern-Sum pro Monat (juengste Monate rechts). 12 Werte fuer Sparkline. */
  umsatzMonthly: number[];
  ebitMonthly: number[];
  /** Anzahl Firmen mit Daten im aktuellen 3M-Fenster (1..n). */
  firmasCovered: number;
  comparison: BwaRolling3MComparison | null;
}

/**
 * Aggregiert BWA-Items zum rollierenden 3-Monats-Schnitt der Konzern-Firmen.
 *
 * Returns null wenn keine Holding-Items vorliegen oder weniger als 1 Monat
 * Daten verfuegbar ist.
 *
 * Berechnung:
 * - Stichtag = max(jahr, monat) ueber Holding-Items.
 * - Aktuelles 3M-Fenster = [Stichtag-2 .. Stichtag], also drei Monate inkl.
 *   Stichtag selbst.
 * - Prior 3M-Fenster = [Stichtag-5 .. Stichtag-3].
 * - Konzern-Sum pro Monat (sum ueber Firmen), dann Durchschnitt ueber die
 *   3 Monate.
 */
export function aggregateBwaRolling3M(
  items: BwaItem[] | undefined | null,
): BwaRolling3MAggregate | null {
  if (!items || items.length === 0) return null;

  const holdingItems = items.filter((it) => HOLDING_FIRMEN.has(it.firma_id));
  if (holdingItems.length === 0) return null;

  // Stichtag = juengster Monat ueber alle Holding-Items.
  let maxJahr = -Infinity;
  let maxMonat = -Infinity;
  for (const it of holdingItems) {
    if (it.jahr > maxJahr || (it.jahr === maxJahr && it.monat > maxMonat)) {
      maxJahr = it.jahr;
      maxMonat = it.monat;
    }
  }
  if (!Number.isFinite(maxJahr)) return null;
  const stichtagKey = maxJahr * 12 + (maxMonat - 1);

  // Konzern-Sum pro Monat-Key.
  const buckets = new Map<number, { umsatz: number; ebit: number; firmas: Set<string> }>();
  for (const it of holdingItems) {
    const key = it.jahr * 12 + (it.monat - 1);
    const prev = buckets.get(key) ?? { umsatz: 0, ebit: 0, firmas: new Set<string>() };
    prev.umsatz += num(it.umsatz_eur);
    prev.ebit += num(it.ergebnis_eur);
    prev.firmas.add(it.firma_id);
    buckets.set(key, prev);
  }

  // Aktuelles 3M-Fenster [stichtag-2 .. stichtag].
  const currentKeys = [stichtagKey - 2, stichtagKey - 1, stichtagKey];
  const currentBuckets = currentKeys.map((k) => buckets.get(k));
  const currentSum = currentBuckets.reduce(
    (acc, b) => ({
      umsatz: acc.umsatz + (b?.umsatz ?? 0),
      ebit: acc.ebit + (b?.ebit ?? 0),
    }),
    { umsatz: 0, ebit: 0 },
  );
  const umsatzAvg = currentSum.umsatz / 3;
  const ebitAvg = currentSum.ebit / 3;
  const margePct = umsatzAvg !== 0 ? (ebitAvg / umsatzAvg) * 100 : null;

  // Prior 3M-Fenster [stichtag-5 .. stichtag-3].
  const priorKeys = [stichtagKey - 5, stichtagKey - 4, stichtagKey - 3];
  const priorBuckets = priorKeys.map((k) => buckets.get(k));
  const hasPrior = priorBuckets.some((b) => b !== undefined);
  let comparison: BwaRolling3MComparison | null = null;
  if (hasPrior) {
    const priorSum = priorBuckets.reduce(
      (acc, b) => ({
        umsatz: acc.umsatz + (b?.umsatz ?? 0),
        ebit: acc.ebit + (b?.ebit ?? 0),
      }),
      { umsatz: 0, ebit: 0 },
    );
    const umsatzPriorAvg = priorSum.umsatz / 3;
    const ebitPriorAvg = priorSum.ebit / 3;
    comparison = {
      umsatzPriorAvg,
      umsatzDeltaPct:
        umsatzPriorAvg > 0
          ? ((umsatzAvg - umsatzPriorAvg) / umsatzPriorAvg) * 100
          : null,
      ebitPriorAvg,
      ebitDeltaAbs: ebitAvg - ebitPriorAvg,
    };
  }

  // 12-Monats-Sparkline ab Stichtag rueckwaerts.
  const umsatzMonthly: number[] = [];
  const ebitMonthly: number[] = [];
  for (let i = 11; i >= 0; i--) {
    const b = buckets.get(stichtagKey - i);
    umsatzMonthly.push(b?.umsatz ?? 0);
    ebitMonthly.push(b?.ebit ?? 0);
  }

  const firmasCovered = new Set<string>();
  for (const b of currentBuckets) {
    if (b) for (const f of b.firmas) firmasCovered.add(f);
  }

  const startKey = stichtagKey - 2;
  const startJahr = Math.floor(startKey / 12);
  const startMonat = (startKey % 12) + 1;

  return {
    jahr: maxJahr,
    bisMonat: maxMonat,
    periodLabel: `${String(startMonat).padStart(2, "0")}-${String(maxMonat).padStart(2, "0")}/${maxJahr}${startJahr !== maxJahr ? ` (ab ${startJahr})` : ""}`,
    umsatzAvg,
    ebitAvg,
    margePct,
    umsatzMonthly,
    ebitMonthly,
    firmasCovered: firmasCovered.size,
    comparison,
  };
}

/**
 * 12M-Umsatz-Serie EINER Firma (Auftrag H Schritt 3 — PerCompany-Sparkline).
 *
 * Anker = juengster Stichtag der Firma selbst (Pahl liefert mit Lag, Firmen
 * sind nicht synchron). Fehlende Monate im Fenster werden 0 — konsistent
 * mit der Konzern-Serie. Weniger als 2 belegte Monate → [] (keine
 * Pseudo-Sparkline aus einem Punkt + Nullen).
 */
export function bwaUmsatzSeriesForFirma(
  items: BwaItem[] | undefined | null,
  firmaId: string,
  trailingMonths = 12,
): number[] {
  if (!items || items.length === 0) return [];
  const firmaItems = items.filter((it) => it.firma_id === firmaId);
  if (firmaItems.length < 2) return [];

  let stichtagKey = -Infinity;
  const buckets = new Map<number, number>();
  for (const it of firmaItems) {
    const key = it.jahr * 12 + (it.monat - 1);
    buckets.set(key, (buckets.get(key) ?? 0) + num(it.umsatz_eur));
    if (key > stichtagKey) stichtagKey = key;
  }
  if (buckets.size < 2) return [];

  const series: number[] = [];
  for (let i = trailingMonths - 1; i >= 0; i--) {
    series.push(buckets.get(stichtagKey - i) ?? 0);
  }
  return series;
}

/**
 * Gesamtumsatz einer Firma in einem Kalenderjahr (Summe der Monatswerte).
 *
 * null wenn kein einziger Monat vorliegt. Achtung: die Dashboard-Query
 * zieht limit=60 Items ueber alle Firmen — ein Vorjahr kann unvollstaendig
 * sein; der RohrPegel behandelt das als Naeherung (Scaffold, Auftrag H
 * Schritt 6).
 */
export function bwaJahresUmsatz(
  items: BwaItem[] | undefined | null,
  firmaId: string,
  jahr: number,
): number | null {
  if (!items) return null;
  let sum = 0;
  let seen = false;
  for (const it of items) {
    if (it.firma_id !== firmaId || it.jahr !== jahr) continue;
    seen = true;
    sum += num(it.umsatz_eur);
  }
  return seen ? sum : null;
}

/** Severity fuer EBIT-Hero (3M-Modus): <0 critical, marge<5% warn, sonst ok. */
export function deriveEbitSeverity3M(
  agg: BwaRolling3MAggregate | null,
): Severity {
  if (!agg) return "ok";
  if (agg.ebitAvg < 0) return "critical";
  if (agg.margePct !== null && agg.margePct < 5) return "warn";
  return "ok";
}

/**
 * Kompakter Geld-Format fuer Hero-Tiles: 557k€ / 1,2 Mio €.
 * Fuer prominente Zahlen mit limitiertem Platz.
 */
export function formatEurCompact(value: number): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? "−" : "";
  if (abs >= 1_000_000) {
    return `${sign}${(abs / 1_000_000).toLocaleString("de-DE", { maximumFractionDigits: 1 })} Mio€`;
  }
  if (abs >= 1_000) {
    return `${sign}${(abs / 1_000).toLocaleString("de-DE", { maximumFractionDigits: 0 })}k€`;
  }
  return `${sign}${abs.toLocaleString("de-DE", { maximumFractionDigits: 0 })}€`;
}

/** "+50%" / "−12%" / "—" Format fuer Vergleichs-Pills. */
export function formatDeltaPct(deltaPct: number | null): string {
  if (deltaPct == null || !Number.isFinite(deltaPct)) return "—";
  const rounded = Math.round(deltaPct);
  if (rounded === 0) return "0%";
  return rounded > 0 ? `+${rounded}%` : `−${Math.abs(rounded)}%`;
}
